import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";

import AssetCard from "./AssetCard";
import ImageModal from "./ImageModal";
import SkeletonResult from "./SkeletonResult";
import PaginationResults from "./PaginationResults";
import FiltersButtons from "./FiltersButtons";
import SortDropdown from "./SortDropdown";
import SettingsButton from "./SettingsButton";
import { useCustomFields } from "../../../contexts/customFields";

export default function Results({
  assets,
  loading,
  pagination,
  defaultValues,
  hideDepletedAssets,
  hasReservedQuantity,
  showReservedFilter,
  onChange,
  processNewItem,
  warehouses = [],
  selectedWarehouseIds = [],
  costCenters = [],
  selectedCostCenterIds = [],
  displayFilters = true,
  displayFiltersButton = true,
  order,
  setOrder,
  project,
  onAssetUpdated,
  centerFilters = false,
  cardsPerRow = 4,
}) {
  const [selectedImage, setSelectedImage] = useState(null);
  const { totalCount = 0 } = pagination;
  const [display] = useCustomFields();
  const selectedCostCenters = costCenters.filter((cc) =>
    selectedCostCenterIds.includes(String(cc.id))
  );

  const selectedAsset = useMemo(
    () => assets.find((asset) => asset.images.find((image) => image.id === selectedImage)),
    [selectedImage]
  );

  const handlePageChange = (_, value) => {
    onChange({ page: value });
  };

  const total = (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <Typography variant="h5" color="textPrimary">
        {`${totalCount} Items`}
      </Typography>
      {selectedCostCenters.map((costCenter) => (
        <Chip
          key={costCenter.id}
          label={`${costCenter.costCenterName || ""}${
            costCenter.costCenterCode ? ` ${costCenter.costCenterCode}` : ""
          }`.trim()}
          size="small"
          onDelete={() =>
            onChange({
              costCenterIds: selectedCostCenterIds.filter((id) => id !== String(costCenter.id)),
              page: 1,
            })
          }
          deleteIcon={<CloseIcon fontSize="small" />}
          sx={{ fontWeight: 500 }}
        />
      ))}
    </Box>
  );

  const filters = displayFilters && (
    <Box mt={centerFilters ? 0 : 1}>
      {displayFiltersButton && (
        <Box mt={centerFilters ? 0 : 1}>
          <FiltersButtons
            hideDepletedAssets={hideDepletedAssets}
            hasReservedQuantity={hasReservedQuantity}
            showReservedFilter={showReservedFilter}
            warehouses={warehouses}
            selectedWarehouseIds={selectedWarehouseIds}
            costCenters={costCenters}
            selectedCostCenterIds={selectedCostCenterIds}
            hideCostCenterFilter={!!project?.groupedAssets || !!project?.groupedSerializedAssets}
            onChange={onChange}
          />
        </Box>
      )}
    </Box>
  );

  return (
    <>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems={centerFilters ? "center" : "flex-start"}
        mb={2}
      >
        {centerFilters ? (
          <>
            {total}
            {filters}
          </>
        ) : (
          <Box>
            {total}
            {filters}
          </Box>
        )}

        <Box display="flex" alignItems="center" gap={1} ml={2}>
          <SortDropdown order={order} setOrder={setOrder} project={project} />
          <SettingsButton
            pagination={pagination}
            onChange={onChange}
            assets={assets}
            processNewItem={processNewItem}
          />
        </Box>
      </Box>
      {loading ? (
        <SkeletonResult cardsPerRow={cardsPerRow} />
      ) : (
        <Grid container spacing={3}>
          {assets.map((asset) => {
            return (
              <Grid
                key={`AssetCard${asset.id}`}
                size={{ lg: cardsPerRow === 3 ? 4 : 3, md: 6, sm: 6, xs: 12 }}
              >
                <AssetCard
                  display={display}
                  defaultValues={defaultValues}
                  onClickImage={(asset, image) => setSelectedImage(image.id)}
                  asset={asset}
                  onSuccess={onAssetUpdated}
                  processNewItem={processNewItem}
                  warehouses={warehouses}
                />
              </Grid>
            );
          })}
        </Grid>
      )}
      {assets.length > 0 && (
        <Box mt={6} display="flex" justifyContent="center">
          <PaginationResults
            id="downPaginationAssets"
            onChange={handlePageChange}
            disabled={loading}
            pagination={pagination}
          />
        </Box>
      )}
      {Boolean(selectedImage) && (
        <ImageModal
          asset={selectedAsset}
          images={selectedAsset?.images || []}
          selectedId={selectedImage}
          onClose={() => setSelectedImage(null)}
        />
      )}
    </>
  );
}
